import type { CSSProperties } from "react";
import { TOP_CHART_MARGIN } from "./perp-chart-view";
import { PriceChart, type PriceChartRefLine } from "./price-chart/price-chart";
import { usePredictionChart } from "../hooks/use-prediction-chart";
import type { PredictionLeg } from "../lib/composePayoff";
import type { SelectedPred } from "../types";

/** Blue-400 — right side prediction chart color. */
export const PRED_COLOR = "#60a5fa";

const emptyStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "var(--f-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--fg-dim)",
  border: "1px dashed var(--line-3)",
  margin: 12,
  borderRadius: 2,
};

function formatCents(v: number): string {
  return `${(v * 100).toFixed(1)}¢`;
}

export function PredictionChartView({
  selected,
  leg,
}: {
  selected: SelectedPred | null;
  leg?: PredictionLeg | null;
}) {
  const { data, currentPrice, error } = usePredictionChart(selected?.yesTokenId);

  if (!selected) {
    return <div style={emptyStyle}>SELECT A PREDICTION</div>;
  }
  if (!selected.yesTokenId) {
    return <div style={emptyStyle}>NO CLOB TOKEN AVAILABLE</div>;
  }
  if (error) {
    return (
      <div style={{ ...emptyStyle, color: "var(--sev-crit)" }}>
        FAILED TO LOAD — {error.message}
      </div>
    );
  }

  const refLines: PriceChartRefLine[] | undefined = leg
    ? [
        {
          value: leg.entryCents / 100,
          color: PRED_COLOR,
          label: `ENTRY ${leg.outcome.toUpperCase()}`,
          dash: "4 4",
          labelAlign: "left",
        },
      ]
    : undefined;

  return (
    <div style={{ flex: 1, minHeight: 0, padding: 8 }}>
      <PriceChart
        data={data}
        lineColor={PRED_COLOR}
        assetName="YES"
        currentPrice={currentPrice}
        curveType="monotoneX"
        valueFormatter={formatCents}
        yTickFormatter={formatCents}
        yDomain={[0, 1]}
        numXTicks={5}
        numYTicks={5}
        margin={TOP_CHART_MARGIN}
        showLiveDot
        refLines={refLines}
      />
    </div>
  );
}
